import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';

import ControlSystemKeysContext from '@totem/components/controlSystemDetail/keys/ControlSystemKeysContext';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey } from '@totem/types/common';
import { CONTROL_SYSTEMS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type FormState = {
  system: string;
  instance: string;
  key: string;
  uid: string;
};

const emptyForm = (): FormState => ({
  system: '',
  instance: '',
  key: '',
  uid: '',
});

type Props = {
  open: boolean;
  keyRecord?: ForeignKey;
  onClose: () => void;
};

const ControlSystemKeyModal = ({ open, keyRecord, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { controlSystemId, requestRefresh } = useContext(
    ControlSystemKeysContext,
  );
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const isEditing = !!keyRecord;

  useEffect(() => {
    if (open) {
      setForm(
        keyRecord
          ? {
              system: keyRecord.system,
              instance: keyRecord.instance,
              key: keyRecord.key,
              uid: keyRecord.uid ?? '',
            }
          : emptyForm(),
      );
    }
  }, [open, keyRecord]);

  const isValid = form.system.trim() && form.instance.trim() && form.key.trim();

  const handleSubmit = useCallback(() => {
    if (!isValid || isSending) {
      return;
    }

    setIsSending(true);

    fetch(`${CONTROL_SYSTEMS_ENDPOINT}/${controlSystemId}/keys`, {
      method: isEditing ? 'PATCH' : 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(form),
    })
      .then((res) => {
        CheckResponseShowError(res);

        requestRefresh();

        onClose();
      })
      .finally(() => setIsSending(false));
  }, [
    form,
    isValid,
    isSending,
    isEditing,
    controlSystemId,
    getApiToken,
    requestRefresh,
    onClose,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={480}
      title={<ModalTitle>{isEditing ? 'Edit Key' : 'Add Key'}</ModalTitle>}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSending}
            disabled={!isValid}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <Form.Item label="System" required>
          <Input
            value={form.system}
            disabled={isEditing}
            onChange={(evt) => setForm({ ...form, system: evt.target.value })}
            placeholder="e.g. Sapient"
          />
        </Form.Item>
        <Form.Item label="Instance" required>
          <Input
            value={form.instance}
            disabled={isEditing}
            onChange={(evt) => setForm({ ...form, instance: evt.target.value })}
            placeholder="e.g. Production"
          />
        </Form.Item>
        <Form.Item label="Key" required>
          <Input
            value={form.key}
            onChange={(evt) => setForm({ ...form, key: evt.target.value })}
            placeholder="e.g. BuildingID"
          />
        </Form.Item>
        <Form.Item label="UID">
          <Input
            value={form.uid}
            onChange={(evt) => setForm({ ...form, uid: evt.target.value })}
            placeholder="Optional external identifier"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ControlSystemKeyModal;
